/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import React, { CSSProperties, FunctionComponent, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { useDrag, useDrop } from 'react-dnd';
import { concat } from '../../common/helpers/arrayHelper';
import { IRowItemData } from '../../common/models/table/IRowItemData';
import DataCell from './DataCell';
import styles from './styles.module.scss';

interface IProps {
  index: number;
  style: CSSProperties;
  data: IRowItemData;
}

const type = 'row';

const TableRow: FunctionComponent<IProps> = React.memo(({
  index,
  style,
  data: {
    editableRowIndex,
    focusedRowId,
    rows,
    rowHeight,
    prepareRow,
    moveRow,
    updateRowEditData,
    onCellClick,
    onRowClick,
    onRowMouseDown,
    onMouseMove,
    isRowDraggable,
    onDragDrop
  }
}) => {
  const row = rows[index];

  if (!row) {
    return (
      <div className={styles.spinnerWrp} style={style}>
        <Spinner size="sm" animation="border" />
      </div>
    );
  }

  prepareRow(row);

  const canDragRow = !isRowDraggable || isRowDraggable(row.original);

  const dropRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop({
    accept: type,
    hover: (item: { index: number; type: string }, monitor) => {
      if (!dropRef.current || !monitor.canDrop()) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex !== hoverIndex) {
        moveRow(dragIndex, hoverIndex);
        item.index = hoverIndex; // eslint-disable-line no-param-reassign
      }
    },
    canDrop: () => canDragRow,
    drop: onDragDrop
  });

  const [{ isDragging }, drag, preview] = useDrag({
    item: { type, index },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
    canDrag: () => canDragRow
  });

  preview(drop(dropRef));
  drag(dragRef);

  const { style: rStyle } = row.getRowProps();
  const rowStyle = { ...rStyle, height: rowHeight };

  const { hint, disabled } = row.original as any;

  const wrapperStyle = {
    ...style,
    minWidth: '100%',
    width: 'auto',
    opacity: isDragging ? 0.3 : 1
  };

  const className = concat(
    'hr-table-row',
    styles.tr,
    row.id === focusedRowId && 'focused',
    row.isSelected && `${styles.selected} selected`,
    hint && styles.trWithHint,
    disabled && styles.disabled,
    canDragRow || styles.dragDisabled
  );

  return (
    <div style={wrapperStyle}>
      <div
        ref={dropRef}
        role="presentation"
        {...row.getRowProps()}
        onClick={() => onRowClick(row)}
        onMouseDown={() => onRowMouseDown(row.original)}
        onMouseMove={() => onMouseMove(row)}
        className={className}
        style={{ ...rowStyle }}
      >
        {row.cells.map(cell => (
          <DataCell
            ref={cell.column.id === 'drag' ? dragRef : null}
            key={cell.column.id}
            cell={cell}
            editableRowIndex={editableRowIndex}
            updateRowEditData={updateRowEditData}
            onClick={onCellClick}
          />
        ))}
        {hint && <div className={styles.trHint}>{hint}</div>}
      </div>
    </div>
  );
});

export default TableRow;
